/**
 * This function exports data to a CSV file.
 * It takes an array of objects and converts it to CSV format.
 * Then it creates a Blob from the CSV string and generates a download link.
 * Finally, it programmatically clicks the link to trigger the download.
 *
 * @param {Array} data - The data to be exported, should be an array of objects.
 * @param {string} fileName - The name of the file to be downloaded (without extension).
 * @returns {void}
 */
export const exportToCsv = (data, fileName) => {
    const csv = convertToCSV(data);

    // Create a Blob from the CSV string
    const blob = new Blob([csv], { type: 'text/csv' });

    // Create a link element to download the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;

    // Append the link to the body and trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Converts an array of objects to a CSV string.
 * Each object represents a row in the CSV.
 * The keys of the first object are used as headers.
 * Each value is enclosed in double quotes and escaped if necessary.
 *
 * @param {Array} data - The data to be converted, should be an array of objects.
 * @returns {string} - The CSV string representation of the data.
 */
export const convertToCSV = (data) => {
    const headers = Object.keys(data[0]);
    const csvRows = [];

    // Add the headers to the first row of the CSV
    csvRows.push(headers.join(','));

    // Add the data rows to the CSV
    for (const row of data) {
        // Create an array of values for each row
        const values = headers.map((header) => {
            // Get the value for the current header
            const escaped = ('' + row[header]).replace(/"/g, '""');
            // Enclose the value in double quotes
            return `"${escaped}"`;
        });

        // Join the values with commas and add to the CSV rows
        csvRows.push(values.join(','));
    }

    // Join all rows with new line characters
    return csvRows.join('\n');
};
